<script lang="ts" setup>
import type { GetRepositoryMonoRepoConfigsQuery } from './MonoRepoConfigModal.generated'
import { Provider } from '~/codegen/graphql'
import {
  CreateMonoRepoConfigDocument,
  DeleteMonoRepoConfigDocument,
  GetRepositoryMonoRepoConfigsDocument,
  UpdateMonoRepoConfigDocument,
} from './MonoRepoConfigModal.generated'

type MonoRepoConfig = NonNullable<
  NonNullable<GetRepositoryMonoRepoConfigsQuery['repository']>['monoRepoConfigs']
>[number]

const props = defineProps<{
  repositoryGid: string
}>()

const open = defineModel<boolean>({ required: true })

const cachePlugin = useGqlCache()

type View = 'list' | 'form'
const currentView = ref<View>('list')
const editingConfig = ref<MonoRepoConfig | null>(null)

const form = useTemplateRef<HTMLFormElement>('form')
const confirmDialog = useTemplateRef<{ reveal: () => Promise<{ isCanceled: boolean }> }>('confirmDialog')

const formState = reactive({
  name: '',
  sourcePathPrefixIncludes: '',
  sourcePathPrefixExcludes: '',
  issueTeamKeys: '',
  issueProvider: 'auto',
  showAdvanced: false,
})

const {
  data: repositoryData,
  isFetching: isLoadingConfigs,
  execute: refetch,
} = useQuery({
  query: GetRepositoryMonoRepoConfigsDocument,
  variables: computed(() => ({ id: props.repositoryGid })),
  paused: computed(() => !open.value),
})

const monoRepoConfigs = computed(() => repositoryData.value?.repository?.monoRepoConfigs ?? [])
const repositoryName = computed(() => {
  const repo = repositoryData.value?.repository
  return repo ? `${repo.owner}/${repo.name}` : ''
})

const { execute: createConfig, isFetching: isCreating } = useMutation(CreateMonoRepoConfigDocument)
const { execute: updateConfig, isFetching: isUpdating } = useMutation(UpdateMonoRepoConfigDocument)
const { execute: deleteConfig, isFetching: isDeleting } = useMutation(DeleteMonoRepoConfigDocument)

const isSaving = computed(() => isCreating.value || isUpdating.value)

const issueProviderOptions = [
  { label: 'Auto-detect', value: 'auto' },
  { label: 'Linear', value: 'linear' },
  { label: 'Jira', value: 'jira' },
]

const configToDelete = ref<MonoRepoConfig | null>(null)
const affectedTeamsOnDelete = ref<string[]>([])

watch(open, (newOpen) => {
  if (newOpen) {
    currentView.value = 'list'
    editingConfig.value = null
    resetForm()
  }
})

function resetForm() {
  formState.name = ''
  formState.sourcePathPrefixIncludes = ''
  formState.sourcePathPrefixExcludes = ''
  formState.issueTeamKeys = ''
  formState.issueProvider = 'auto'
  formState.showAdvanced = false
}

function startCreate() {
  editingConfig.value = null
  resetForm()
  currentView.value = 'form'
}

function startEdit(config: MonoRepoConfig) {
  editingConfig.value = config
  formState.name = config.name ?? ''
  // Convert arrays to comma-separated strings for the form
  formState.sourcePathPrefixIncludes = (config.sourcePathPrefixIncludes ?? []).join(', ')
  formState.sourcePathPrefixExcludes = (config.sourcePathPrefixExcludes ?? []).join(', ')
  formState.issueTeamKeys = (config.issueTeamKeys ?? []).join(', ')
  // Convert enum value to lowercase for display, or 'auto' if not set
  formState.issueProvider = config.issueProvider?.toLowerCase() ?? 'auto'
  formState.showAdvanced = !!((config.issueTeamKeys && config.issueTeamKeys.length > 0) || config.issueProvider)
  currentView.value = 'form'
}

function backToList() {
  currentView.value = 'list'
  editingConfig.value = null
  resetForm()
}

function csvToArray(csv: string): string[] | null {
  if (!csv.trim()) return null
  return csv
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

function getProvider(value: string): Provider | null {
  if (value === 'linear') return Provider.Linear
  if (value === 'jira') return Provider.Jira
  return null
}

async function onSubmit() {
  const nameInput: HTMLInputElement | null = form.value?.$el?.querySelector('[name="name"]')
  if (nameInput && !nameInput.checkValidity()) {
    nameInput.reportValidity()
    return
  }

  let response

  if (editingConfig.value) {
    response = await updateConfig({
      input: {
        id: editingConfig.value.id!,
        name: formState.name,
        sourcePathPrefixIncludes: csvToArray(formState.sourcePathPrefixIncludes),
        sourcePathPrefixExcludes: csvToArray(formState.sourcePathPrefixExcludes),
        issueTeamKeys: csvToArray(formState.issueTeamKeys),
        issueProvider: getProvider(formState.issueProvider),
      },
    })
    const error = handleMutationError({
      errors: response.data?.updateMonoRepoConfig?.errors,
      combinedError: response.error,
    })
    if (error) return
  } else {
    response = await createConfig({
      input: {
        repositoryId: props.repositoryGid,
        name: formState.name,
        sourcePathPrefixIncludes: csvToArray(formState.sourcePathPrefixIncludes),
        sourcePathPrefixExcludes: csvToArray(formState.sourcePathPrefixExcludes),
        issueTeamKeys: csvToArray(formState.issueTeamKeys),
        issueProvider: getProvider(formState.issueProvider),
      },
    })
    const error = handleMutationError({
      errors: response.data?.createMonoRepoConfig?.errors,
      combinedError: response.error,
    })
    if (error) return
  }

  cachePlugin.clearCache()
  await refetch()
  backToList()
}

async function confirmDelete(config: MonoRepoConfig) {
  configToDelete.value = config
  affectedTeamsOnDelete.value = (config.teamsUsing ?? []).filter((t): t is string => t !== null)

  const result = await confirmDialog.value?.reveal()
  if (result?.isCanceled) {
    configToDelete.value = null
    affectedTeamsOnDelete.value = []
    return
  }

  const response = await deleteConfig({ id: config.id! })
  const error = handleMutationError({
    errors: response.data?.deleteMonoRepoConfig?.errors,
    combinedError: response.error,
  })

  configToDelete.value = null
  affectedTeamsOnDelete.value = []

  if (error) return

  cachePlugin.clearCache()
  await refetch()
}
</script>

<template>
  <ConfirmDialog ref="confirmDialog" is-delete :loading="isDeleting">
    <template #title> Delete mono-repo configuration </template>
    <p class="text-gray-600 dark:text-gray-400">
      Are you sure you want to delete <strong>{{ configToDelete?.name }}</strong
      >?
    </p>
    <p v-if="affectedTeamsOnDelete.length > 0" class="mt-2 text-sm text-amber-600 dark:text-amber-400">
      This configuration is used by the following teams: {{ affectedTeamsOnDelete.join(', ') }}
    </p>
  </ConfirmDialog>

  <UModal v-model="open" :ui="{ width: 'w-full sm:max-w-xl' }">
    <ModalContentWrapper
      :title="
        currentView === 'list' ? 'Mono-repo Configurations' : editingConfig ? 'Edit Configuration' : 'Add Configuration'
      "
      :subtitle="currentView === 'list' ? repositoryName : undefined"
      :scrollable-content="false"
      @close="open = false"
    >
      <template v-if="currentView === 'form'" #backButton>
        <UButton icon="light:arrow-left" variant="ghost" color="gray" size="sm" @click="backToList" />
      </template>

      <!-- List View -->
      <div v-if="currentView === 'list'" class="flex flex-col gap-4">
        <div v-if="isLoadingConfigs" class="flex items-center justify-center py-8">
          <UIcon name="i-heroicons-arrow-path" class="size-6 animate-spin text-gray-400" />
        </div>

        <template v-else-if="monoRepoConfigs.length > 0">
          <div
            v-for="config in monoRepoConfigs"
            :key="config.id!"
            class="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700"
          >
            <div class="flex flex-col">
              <span class="font-medium text-gray-900 dark:text-white">
                {{ config.name }}
              </span>
              <span v-if="config.sourcePathPrefixIncludes" class="text-xs text-gray-500 dark:text-gray-400">
                Paths: {{ config.sourcePathPrefixIncludes }}
              </span>
              <span
                v-if="config.teamsUsing && config.teamsUsing.length > 0"
                class="text-xs text-gray-500 dark:text-gray-400"
              >
                Used by: {{ config.teamsUsing.join(', ') }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <UButton icon="light:pencil" variant="ghost" color="gray" size="xs" @click="startEdit(config)" />
              <UButton
                icon="light:trash-can"
                variant="ghost"
                color="red"
                size="xs"
                :loading="isDeleting && configToDelete?.id === config.id"
                @click="confirmDelete(config)"
              />
            </div>
          </div>
        </template>

        <div v-else class="py-8 text-center text-sm text-gray-500">
          No mono-repo configurations yet.
          <br />
          Create one to define path-based scopes for skills.
        </div>
      </div>

      <!-- Form View -->
      <div v-else class="flex flex-col gap-4">
        <UForm ref="form" :state="formState" @submit="onSubmit">
          <FormGroup
            label="Name"
            required
            class="mb-4"
            size="md"
            name="name"
            help="A descriptive name for this configuration (e.g., 'Frontend', 'Backend API')"
          >
            <UInput v-model="formState.name" autofocus required placeholder="e.g., Frontend" />
          </FormGroup>

          <FormGroup
            label="Source Path Includes"
            size="md"
            name="sourcePathPrefixIncludes"
            help="Comma-separated paths to include (e.g., src/frontend,packages/ui)"
            class="mb-4"
          >
            <UInput v-model="formState.sourcePathPrefixIncludes" placeholder="e.g., src/frontend,packages/ui" />
          </FormGroup>

          <FormGroup
            label="Source Path Excludes"
            size="md"
            name="sourcePathPrefixExcludes"
            help="Comma-separated paths to exclude from this configuration"
            class="mb-4"
          >
            <UInput v-model="formState.sourcePathPrefixExcludes" placeholder="e.g., node_modules,dist" />
          </FormGroup>

          <div class="mb-4">
            <button
              type="button"
              class="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600"
              @click="formState.showAdvanced = !formState.showAdvanced"
            >
              <UIcon
                name="solid:caret-right"
                class="size-3 transition-transform duration-200"
                :class="{ 'rotate-90': formState.showAdvanced }"
              />
              <span>Advanced settings</span>
            </button>
          </div>

          <template v-if="formState.showAdvanced">
            <FormGroup
              label="Issue Team Keys"
              size="md"
              name="issueTeamKeys"
              help="Comma-separated team identifiers in your issue tracker"
              class="mb-4"
            >
              <UInput v-model="formState.issueTeamKeys" placeholder="e.g., FRONT,UI" />
            </FormGroup>

            <FormGroup
              label="Issue Provider"
              size="md"
              name="issueProvider"
              help="Override the issue tracking provider for this configuration"
            >
              <USelectMenu
                v-model="formState.issueProvider"
                :options="issueProviderOptions"
                value-attribute="value"
                option-attribute="label"
              />
            </FormGroup>
          </template>
        </UForm>
      </div>

      <template #footer>
        <template v-if="currentView === 'list'">
          <UButton variant="ghost" color="gray" @click="open = false"> Close </UButton>
          <UButton color="primary" @click="startCreate"> Add Configuration </UButton>
        </template>
        <template v-else>
          <UButton variant="ghost" color="gray" @click="backToList"> Cancel </UButton>
          <UButton color="primary" :loading="isSaving" @click="form?.submit()">
            {{ editingConfig ? 'Save' : 'Create' }}
          </UButton>
        </template>
      </template>
    </ModalContentWrapper>
  </UModal>
</template>
